// Sidebar.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  DocumentTextIcon,
  UserGroupIcon,
  PlusCircleIcon,
  TableCellsIcon,
  RectangleGroupIcon,
  UserIcon,
  ChartBarIcon,
  HomeIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [role, setRole] = useState("");
  const location = useLocation();

  useEffect(() => {
    const storedUserData = sessionStorage.getItem("user");
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      setRole(userData.role);
    }
  }, []);

  const isActiveLink = (path) => location.pathname === path;

  const menuSections = [
    {
      title: "Dashboard",
      show: role === "admin",
      items: [
        { path: "/admin/", icon: HomeIcon, label: "Dashboard" },
      ]
    },
    {
      title: "Questions",
      show: role === "admin",
      items: [
        { path: "/admin/que/add", icon: PlusCircleIcon, label: "Add Question" },
        { path: "/admin/que", icon: TableCellsIcon, label: "All Questions" },
        { path: "/admin/quegrp", icon: RectangleGroupIcon, label: "Question Groups" },
      ]
    },
    {
      title: "Exams",
      show: role === "admin",
      items: [
        { path: "/admin/exam/add", icon: PlusCircleIcon, label: "Create Exam" },
        { path: "/admin/exam", icon: DocumentTextIcon, label: "All Exams" },
      ]
    },
    {
      title: "Users & Results",
      show: true,
      items: [
        { path: "/admin/users", icon: UserIcon, label: "All Users" },
        { path: "/admin/get-users", icon: UserGroupIcon, label: "Exam Status" },
        ...(role === "admin" ? [{ path: "/admin/result", icon: ChartBarIcon, label: "Results" }] : []),
      ]
    },
  ];

  return (
    <div 
      className={`${isExpanded ? 'w-64' : 'w-20'} h-screen bg-white flex flex-col transition-all duration-300 ease-in-out fixed left-0 top-0 z-30 border-r border-gray-200 shadow-sm`}
    >
      {/* Logo/Brand */}
      <div className="h-20 flex items-center justify-between px-4 border-b border-gray-100 flex-shrink-0">
        {isExpanded ? (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg">E</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800">EduKota</h1>
              <p className="text-xs text-gray-500">Admin Panel</p>
            </div>
          </div>
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center mx-auto shadow-sm">
            <span className="text-white font-bold text-lg">E</span>
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute -right-3 top-24 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-blue-700 transition-all hover:scale-110 z-40"
        aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {isExpanded ? (
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        ) : (
          <ChevronRightIcon className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Navigation */}
      <nav className="flex-1 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {menuSections.map((section, sectionIndex) => (
          section.show && (
            <div key={sectionIndex} className="mb-6">
              {isExpanded && (
                <div className="px-6 mb-3">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    {section.title}
                  </p>
                </div>
              )}
              <ul className="space-y-1 px-3">
                {section.items.map((item, itemIndex) => {
                  const Icon = item.icon;
                  const isActive = isActiveLink(item.path);
                  
                  return (
                    <li key={itemIndex}>
                      <Link
                        to={item.path}
                        className={`group flex items-center h-11 rounded-lg transition-all duration-200 relative ${
                          isActive 
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600' 
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } ${isExpanded ? 'px-4' : 'justify-center'}`}
                        title={!isExpanded ? item.label : ''}
                      >
                        {isActive && (
                          <div className="absolute left-0 w-1 h-7 bg-blue-600 rounded-r-full"></div>
                        )}
                        
                        <Icon className={`h-5 w-5 flex-shrink-0 transition-colors ${
                          isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                        }`} />
                        
                        {isExpanded && (
                          <span className={`ml-3 text-sm font-medium whitespace-nowrap ${
                            isActive ? 'text-blue-600' : 'text-gray-700'
                          }`}>
                            {item.label}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )
        ))}
      </nav>


    </div>
  );
};

export default Sidebar;
